import React from 'react';
import { useTranslation } from 'react-i18next';

interface TermsOfServiceProps {
  onNavigate: (page: string) => void;
}

const TermsOfService: React.FC<TermsOfServiceProps> = ({ onNavigate }) => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-sm rounded-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('termsOfService.title')}</h1>
          
          <div className="prose max-w-none">
            <p className="text-lg text-gray-600 mb-6">
              {t('termsOfService.lastUpdated', { date: new Date().toLocaleDateString() })}
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('termsOfService.sections.agreementToTerms.title')}</h2>
              <p className="text-gray-700 mb-4">
                {t('termsOfService.sections.agreementToTerms.content')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('termsOfService.sections.descriptionOfServices.title')}</h2>
              <p className="text-gray-700 mb-4">
                {t('termsOfService.sections.descriptionOfServices.content')}
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                {t('termsOfService.sections.descriptionOfServices.list', { returnObjects: true }).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              <p className="text-gray-700 mb-4">
                {t('termsOfService.sections.descriptionOfServices.additionalInfo')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('termsOfService.sections.userAccounts.title')}</h2>
              <p className="text-gray-700 mb-4">
                {t('termsOfService.sections.userAccounts.content')}
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                {t('termsOfService.sections.userAccounts.list', { returnObjects: true }).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('termsOfService.sections.acceptableUse.title')}</h2>
              <p className="text-gray-700 mb-4">{t('termsOfService.sections.acceptableUse.content')}</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                {t('termsOfService.sections.acceptableUse.list', { returnObjects: true }).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('termsOfService.sections.subscriptionPlans.title')}</h2>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('termsOfService.sections.subscriptionPlans.subscriptionPlans.title')}</h3>
              <p className="text-gray-700 mb-4">
                {t('termsOfService.sections.subscriptionPlans.subscriptionPlans.content')}
              </p>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('termsOfService.sections.subscriptionPlans.paymentTerms.title')}</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                {t('termsOfService.sections.subscriptionPlans.paymentTerms.list', { returnObjects: true }).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('termsOfService.sections.subscriptionPlans.freeTrial.title')}</h3>
              <p className="text-gray-700 mb-4">
                {t('termsOfService.sections.subscriptionPlans.freeTrial.content')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('termsOfService.sections.intellectualProperty.title')}</h2>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('termsOfService.sections.intellectualProperty.ourRights.title')}</h3>
              <p className="text-gray-700 mb-4">
                {t('termsOfService.sections.intellectualProperty.ourRights.content')}
                
                {t('termsOfService.sections.intellectualProperty.ourRights.openSource').replace('{{kexaLink}}', '')} <a 
                  href="https://github.com/kexa-io/Kexa" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary font-medium hover:underline"
                >
                  Kexa
                </a>
              </p>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('termsOfService.sections.intellectualProperty.userData.title')}</h3>
              <p className="text-gray-700 mb-4">
                {t('termsOfService.sections.intellectualProperty.userData.content')}
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('termsOfService.sections.intellectualProperty.feedback.title')}</h3>
              <p className="text-gray-700 mb-4">
                {t('termsOfService.sections.intellectualProperty.feedback.content')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('termsOfService.sections.dataProtection.title')}</h2>
              <p className="text-gray-700 mb-4">
                {t('termsOfService.sections.dataProtection.content')}{' '}
                <button onClick={() => onNavigate('privacy-policy')} className="text-gradient font-medium hover:underline">
                  {t('termsOfService.sections.dataProtection.privacyPolicyLink')}
                </button>
                {t('termsOfService.sections.dataProtection.continuedText')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('termsOfService.sections.serviceAvailability.title')}</h2>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('termsOfService.sections.serviceAvailability.availability.title')}</h3>
              <p className="text-gray-700 mb-4">
                {t('termsOfService.sections.serviceAvailability.availability.content')}
              </p>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('termsOfService.sections.serviceAvailability.support.title')}</h3>
              <p className="text-gray-700 mb-4">
                {t('termsOfService.sections.serviceAvailability.support.content')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('termsOfService.sections.termination.title')}</h2>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('termsOfService.sections.termination.terminationByYou.title')}</h3>
              <p className="text-gray-700 mb-4">
                {t('termsOfService.sections.termination.terminationByYou.content')}
              </p>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('termsOfService.sections.termination.terminationByUs.title')}</h3>
              <p className="text-gray-700 mb-4">
                {t('termsOfService.sections.termination.terminationByUs.content')}
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('termsOfService.sections.termination.effectOfTermination.title')}</h3>
              <p className="text-gray-700 mb-4">
                {t('termsOfService.sections.termination.effectOfTermination.content')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('termsOfService.sections.disclaimers.title')}</h2>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('termsOfService.sections.disclaimers.disclaimer.title')}</h3>
              <p className="text-gray-700 mb-4">
                {t('termsOfService.sections.disclaimers.disclaimer.content')}
              </p>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('termsOfService.sections.disclaimers.limitationOfLiability.title')}</h3>
              <p className="text-gray-700 mb-4">
                {t('termsOfService.sections.disclaimers.limitationOfLiability.content')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('termsOfService.sections.governingLaw.title')}</h2>
              <p className="text-gray-700 mb-4">
                {t('termsOfService.sections.governingLaw.content')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('termsOfService.sections.modifications.title')}</h2>
              <p className="text-gray-700 mb-4">
                {t('termsOfService.sections.modifications.content')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('termsOfService.sections.contact.title')}</h2>
              <p className="text-gray-700 mb-4">
                {t('termsOfService.sections.contact.content')}
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                {t('termsOfService.sections.contact.list', { returnObjects: true }).map((item: string, index: number) => (
                  <li key={index}>
                    {index === 1 ? (
                      <>
                        Website: <button onClick={() => onNavigate('contact')} className="text-gradient font-medium hover:underline">Contact page</button>
                      </>
                    ) : (
                      item
                    )}
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;